import { DropdownOption, mapToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import React, { Component } from "react";
import { Dimmer, Grid, Loader, Message } from 'semantic-ui-react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import Input from 'components/final-form/Input';
import { debounce } from 'ts-debounce';
import { SubCostCenterDto, UpsertSubCostCenterDto } from 'ts-types/api.types';
import { FormState } from 'final-form';
import axios, { CancelTokenSource } from 'axios';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import CheckBox from 'components/final-form/CheckBox';
import { required } from 'utils/validatorUtils';
import { noop } from 'utils/functionUtils';
import {
  addSubCostCenter,
  deleteSubCostCenter,
  editSubCostCenter,
  searchSubCostCentersByDescription
} from 'services/subCostCenterServices';
import { getAllCostCenters } from 'services/costCenterServices';
import Select from 'components/final-form/Select';
import { errorUtils } from 'utils/errorUtils';
import DeleteActionButton from 'components/DeleteActionButton';
import PxGrid from 'components/layout/PxGrid';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import EditActionButton from 'components/EditActionButton';
import AddButton from 'components/AddButton';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import SaveActionButton from 'components/SaveActionButton';
import CancelActionButton from 'components/CancelActionButton';
import SearchResultComponent from 'components/SearchResultComponent';

interface CostCenterSearchRequest {
  description: string;
}

interface CostCenterFieldProps extends FormState {
  values: Partial<CostCenterSearchRequest>
}

interface Props extends MessageSourceProps {
}

interface State {
  foundSubCostCenters: Array<SubCostCenterDto>,
  subCostCentersLoaded: boolean,
  upsertSubCostCenter?: UpsertSubCostCenterDto,
  upsertActive: boolean,
  loading: boolean,
  costCenters: Array<DropdownOption>,
  searchValues: string,
  cancelTokenSource: CancelTokenSource,
  errorMessage?: string
}

class SubCostCenterView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundSubCostCenters: [],
      subCostCentersLoaded: false,
      upsertSubCostCenter: undefined,
      upsertActive: false,
      costCenters: [],
      searchValues: "",
      loading: false,
      cancelTokenSource
    };

    getAllCostCenters(cancelTokenSource)
        .then(response => {
          this.setState({
            costCenters: mapToDropdownOptionArray(response, "description")
          })
        })
        .catch(noop);
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    const {cancelTokenSource} = this.state;

    this.mounted = false;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  newCostCenter = () => {
    const {foundSubCostCenters, upsertActive, costCenters} = this.state;

    const defaultValues: SubCostCenterDto = {
      id: -1,
      description: "",
      costCenterId: costCenters[0].value,
      baseSubCenter: false
    };

    this.setState({
      foundSubCostCenters: [defaultValues, ...foundSubCostCenters],
      upsertSubCostCenter: defaultValues,
      upsertActive: !upsertActive
    })
  };

  editCostCenter = (id: number) => {

    const {foundSubCostCenters, upsertActive} = this.state;
    const costCenter = foundSubCostCenters.find((costCenter) => {
      return costCenter.id == id
    });

    this.setState({
      upsertActive: !upsertActive,
      upsertSubCostCenter: costCenter
    });
  };

  searchAfterSubmit = () => {
    const {searchValues, cancelTokenSource} = this.state;

    this.setState({
      subCostCentersLoaded: false
    });

    const onFinally = () => {
      this.setState({
        subCostCentersLoaded: true
      })
    };

    searchSubCostCentersByDescription(searchValues, cancelTokenSource)
        .then(response => {
          this.setState({
            foundSubCostCenters: response,
            upsertActive: false
          });
        })
        .catch(noop)
        .finally(onFinally);
  };

  handleSubmit = (values: Partial<UpsertSubCostCenterDto>) => {

    const {upsertSubCostCenter, cancelTokenSource} = this.state;

    if (upsertSubCostCenter && upsertSubCostCenter.id != -1) {
      editSubCostCenter(upsertSubCostCenter.id, cancelTokenSource, values)
          .then(this.searchAfterSubmit)
          .catch(error => this.handleError(error.response.data));
    } else {
      addSubCostCenter(values, cancelTokenSource)
          .then(this.searchAfterSubmit)
          .catch(error => this.handleError(error.response.data));
    }
  };

  handleError(error: any) {
    const {getMessage} = this.props;

    const errorCode = error.errorCode;
    const knownErrors = [errorUtils.deletingReferencedSubCostCenterNotAllowed];
    if (knownErrors.includes(errorCode)) {
      this.setState({
        errorMessage: getMessage(`sub.cost.center.error.${errorCode}`)
      });
    } else {
      this.setState({
        errorMessage: getMessage('error.general')
      })
    }
  }

  onCancel = () => {
    this.setState(({foundSubCostCenters, upsertActive}: State) => ({
      foundSubCostCenters: foundSubCostCenters.filter(costCenter => costCenter.id != -1),
      upsertSubCostCenter: undefined,
      upsertActive: !upsertActive
    }));
  };

  deleteCostCenter = (id: number): void => {
    const {foundSubCostCenters, cancelTokenSource} = this.state;

    deleteSubCostCenter(id, cancelTokenSource)
        .then(() => {
          this.setState({
            foundSubCostCenters: foundSubCostCenters.filter(subCostCenter => subCostCenter.id != id)
          })
        })
        .catch(error => this.handleError(error.response.data));
  };

  handleCostCenterChange = debounce(({values}: CostCenterFieldProps): void => {
    if (!this.mounted) {
      return;
    }

    const {description = ""} = values;
    const {cancelTokenSource} = this.state;

    this.setState({
      subCostCentersLoaded: false,
      searchValues: description
    });

    const onFinally = () => {
      this.setState({
        subCostCentersLoaded: true
      })
    };

    searchSubCostCentersByDescription(description, cancelTokenSource)
        .then(response => {
          this.setState({
            foundSubCostCenters: response
          });
        })
        .catch(noop)
        .finally(onFinally);
  }, 300);

  render(): React.ReactNode {
    const {subCostCentersLoaded} = this.state;

    const costCenterView = (
        <PraxStyledSegment>

          <div>
            {this.renderSearchSubCostCenterSegment()}
          </div>

          <StyledViewTableRow>
            {!subCostCentersLoaded ? this.renderLoader() : this.renderCostCentersSegment()}
          </StyledViewTableRow>

        </PraxStyledSegment>
    );
    return (
        <React.Fragment>
          {costCenterView}
        </React.Fragment>
    );
  }

  renderSearchSubCostCenterSegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {upsertActive, errorMessage, foundSubCostCenters} = this.state;

    const numResults = foundSubCostCenters.length;

    return (
        <PxGrid>
          {errorMessage && this.renderErrorSegment()}
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("select")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="description"
                  component={Input}
                  fluid
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <AddButton buttonMessage={getMessage("button.add")} disabled={upsertActive}
                         onClick={() => this.newCostCenter()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleCostCenterChange}/>
        </PxGrid>
    )
  };

  descriptionCellRenderer = ({rowData, cellData, dataKey}: any) => {
    const {upsertActive, upsertSubCostCenter} = this.state;
    const costCenterId = rowData.id;

    if (upsertSubCostCenter && costCenterId == upsertSubCostCenter.id && upsertActive) {
      const validator = (value: string) => required(value);
      return (
          <React.Fragment>
            <Field
                name={dataKey}
                component={Input}
                validate={validator}
                autoFocus
            />
          </React.Fragment>
      );
    }

    return (
        <React.Fragment>
          {cellData}
        </React.Fragment>
    );
  };

  baseSubCenterCellRenderer = ({rowData, cellData, dataKey}: any) => {
    const {getMessage} = this.props;
    const {upsertActive, upsertSubCostCenter} = this.state;
    const costCenterId = rowData.id;

    if (upsertSubCostCenter && costCenterId == upsertSubCostCenter.id && upsertActive) {
      return (
          <React.Fragment>
            <Field
                name={dataKey}
                component={CheckBox}
            />
          </React.Fragment>
      );
    }

    return (
        <React.Fragment>
          {cellData && getMessage("sub.cost.center.base")}
        </React.Fragment>
    );
  };

  costCenterCellRenderer = ({rowData, cellData}: any) => {
    const {getMessage} = this.props;
    const {upsertActive, upsertSubCostCenter, costCenters} = this.state;
    const costCenterId = rowData.id;

    if (upsertSubCostCenter && costCenterId == upsertSubCostCenter.id && upsertActive) {
      return (
          <React.Fragment>
            <Field
                name="costCenterId"
                component={Select}
                placeholder={`${getMessage('select')} ${getMessage('treatmentForm.costCenter')}`}
                options={costCenters}
                fluid
            />
          </React.Fragment>
      );
    }

    const costCenter = costCenters && costCenters.find(cc => cc.value === cellData);
    return (
        <React.Fragment>
          {costCenter && costCenter.text}
        </React.Fragment>
    );
  };

  actionsCellRenderer = ({rowData, cellData}: any) => {
    const {getMessage} = this.props;
    const {upsertActive, upsertSubCostCenter} = this.state;
    const costCenterId = rowData.id;

    if (upsertSubCostCenter && costCenterId == upsertSubCostCenter.id && upsertActive) {
      return (
          <React.Fragment>
            <SaveActionButton hoverMessage={getMessage('button.save')}/>
            <CancelActionButton hoverMessage={getMessage('button.cancel')} onConfirm={() => this.onCancel()}/>
          </React.Fragment>
      );
    }

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.editCostCenter(cellData)}
              disabled={upsertActive}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("sub.cost.center.confirmDelete")}
              onConfirm={() => this.deleteCostCenter(cellData)}
              disabled={upsertActive}
          />
        </React.Fragment>
    );
  };

  rowGetter = ({index}: any) => {
    const {foundSubCostCenters} = this.state;

    Object.assign(foundSubCostCenters[index], {index: index + 1});

    return foundSubCostCenters[index];
  };

  foundSubCostCentersRowRenderer = ({className, columns, index, key, style, rowData}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {upsertSubCostCenter, upsertActive} = this.state;
    const costCenterId = rowData.id;

    if (upsertSubCostCenter && costCenterId == upsertSubCostCenter.id && upsertActive) {

      let styleCopy = style;
      styleCopy.overflow = "visible";

      style = {
        position: "relative"
      };

      return (
          <div style={style}
               key={key}
          >
            <FinalForm
                onSubmit={this.handleSubmit}
                initialValues={upsertSubCostCenter}
                subscription={{pristine: true}}
                render={({handleSubmit}) => (
                    <form onSubmit={handleSubmit}
                          {...a11yProps}
                          className={className}
                          role="row"
                          style={styleCopy}
                    >

                      {columns}
                    </form>
                )}
            />
          </div>
      );
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
        >
          {columns}
        </div>
    );
  };

  renderCostCentersSegment = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {foundSubCostCenters} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundSubCostCenters.length}
            rowGetter={this.rowGetter}
            rowRenderer={this.foundSubCostCentersRowRenderer}
            rowHeight={42}
            scrollToIndex={0}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("description")),
                dataKey: "description",
                cellRenderer: (this.descriptionCellRenderer)
              },
              {
                width: 300,
                label: (getMessage("sub.cost.center.base")),
                dataKey: "baseSubCenter",
                cellRenderer: (this.baseSubCenterCellRenderer)
              },
              {
                width: 200,
                flexGrow: 1,
                label: (getMessage("sub.cost.center.cc")),
                dataKey: "costCenterId",
                cellRenderer: (this.costCenterCellRenderer),
                customClass: "dropdown"
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

  renderLoader = (): React.ReactNode => {
    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">{getMessage("sub.cost.center.loading")}</Loader>
        </Dimmer>
    )
  };

  renderErrorSegment = (): React.ReactNode => {
    const {errorMessage} = this.state;

    return (
        <Grid.Row>
          <Grid.Column>
            <Message negative>
              <p>{errorMessage}</p>
            </Message>
          </Grid.Column>
        </Grid.Row>
    );
  };

}

export default withMessages(SubCostCenterView);
